import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { type Article } from "./types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Returns specific Tailwind classes for category color system.
 * soft background + bright text look.
 */
export function getCategoryColorClass(category: string = ''): string {
  const cat = (category || '').toLowerCase().trim();
  if (cat.includes('politics')) return 'text-politics border-politics/40';
  if (cat.includes('business')) return 'text-business border-business/40';
  if (cat.includes('economy')) return 'text-economy border-economy/40';
  if (cat.includes('technology')) return 'text-technology border-technology/40';
  if (cat.includes('crime')) return 'text-crime border-crime/40';
  if (cat.includes('world')) return 'text-world border-world/40';
  if (cat.includes('lifestyle') || cat.includes('health')) return 'text-health border-health/40';
  if (cat.includes('education')) return 'text-education border-education/40';
  if (cat.includes('sports')) return 'text-sports border-sports/40';
  if (cat.includes('security')) return 'text-security border-security/40';
  if (cat.includes('jobs')) return 'text-jobs border-jobs/40';
  return 'text-white border-white/20';
}

/**
 * Checks if a URL points to a common video file format.
 */
export function isVideoUrl(url?: string): boolean {
  if (!url || typeof url !== 'string') return false;
  // Handle URLs with query parameters correctly
  return !!url.split('?')[0].match(/\.(mp4|webm|ogg|mov)$/i);
}

/**
 * Standardized category color - brand red.
 */
export function getCategoryColor(category?: string): string {
  return 'bg-primary text-primary-foreground';
}

/**
 * Extremely robust date parsing for Firestore Timestamps, ISO strings, and JS Dates.
 * Returns a valid Date object or a fallback to now.
 */
export function parseDate(dateVal: any): Date {
  if (!dateVal) return new Date();
  
  try {
    // Firestore Timestamp { seconds, nanoseconds }
    if (typeof dateVal === 'object' && dateVal !== null && 'seconds' in dateVal) {
      return new Date(dateVal.seconds * 1000);
    }

    // ISO String or other date string
    if (typeof dateVal === 'string') {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? new Date() : d;
    }

    // Date object
    if (dateVal instanceof Date) {
      return isNaN(dateVal.getTime()) ? new Date() : dateVal;
    }

    // Number (milliseconds)
    if (typeof dateVal === 'number') {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? new Date() : d;
    }
  } catch (e) {
    return new Date();
  }

  return new Date();
}

/**
 * Calculates approximate reading time for a list of content blocks.
 */
export function calculateReadingTime(body?: any[]): number {
  if (!body || !Array.isArray(body)) return 1;
  const words = body
    .map(block => block.text || '')
    .join(' ')
    .split(/\s+/)
    .length;
  return Math.max(1, Math.ceil(words / 225));
}
